import { animate, state, style, transition, trigger } from '@angular/animations';
import { DatePipe } from '@angular/common';
import { AfterViewInit, Component, Inject, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import * as moment from 'moment';
import { of, Subscription } from 'rxjs';
import { mergeAll } from 'rxjs/operators';
import { IBreadcrumbData } from '../../../../interfaces/breadcrumb-data.interface';
import { NotificationService } from '../../../../services/notification.service';
import { WebSocketService } from '../../../../services/web-socket.service';
import { CoreConfig } from '../../../../utils/config';
import { MovementDataSource } from '../../data-sources/movement.data-source';
import { MovementsService } from '../../services/movements.service';

class MovementsNames {
  static readonly result = {
    0: 'OK',
    1: 'Error lectura matrícula',
    2: 'No encontrado',
    3: 'No autorizado',
    7: 'Error indefinido',
    14: 'Antipassback',
    21: 'Fuera de horario',
    22: 'Sin estancia presente',
    23: 'Límite de vehículos',
    60: 'Matrícula no coincide',
    61: 'Carga especial',
    62: 'Overload',
    63: 'Underload',
    64: 'Verificar carga',
    99: 'No entrado',
  };

  static readonly opening = {
    0: 'No entrado',
    1: 'Automática',
    2: 'Manual',
    5: 'Validación externa',
    6: 'Freeflow',
  };
}

@Component({
  selector: 'app-list',
  templateUrl: './list.component.html',
  styleUrls: ['./list.component.scss'],
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({ height: '0px', minHeight: '0' })),
      state('expanded', style({ height: '*' })),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
  ],
})
export class ListComponent implements OnInit, AfterViewInit {
  /**
   * Displayed columns array
   */
  displayedColumns: string[] = [
    'icon',
    'movementId',
    'createdAt',
    'unifiedName',
    // 'clientName',
    // 'visitor',
    // 'owner',
    'door',
    'direction',
    'carPlateAssigned',
    'cardAssigned',
    'result',
    'opening',
  ];

  /**
   * Table config array
   */
  tableConfig: any[] = [
    {
      columnName: '',
    },
    {
      columnName: 'id',
      enableSort: true,
    },
    {
      columnName: 'Fecha',
      pipe: new DatePipe('es'),
      configPipe: ['d MMM y, H:mm'],
      enableSort: true,
    },
    {
      columnName: 'Cliente/Visita/Propietario',
    },
    {
      columnName: 'Puerta',
    },
    {
      columnName: 'Direccion',
    },
    {
      columnName: 'Matrícula asignada',
    },
    {
      columnName: 'Tarjeta asignada',
    },
    {
      columnName: 'Resultado',
      pipe: (value: any) => {
        return MovementsNames.result[value] || value;
      },
    },
    {
      columnName: 'Apertura',
      pipe: (value: any) => {
        return MovementsNames.opening[value] || value;
      },
    },
  ];

  /**
   * Movements data source
   */
  movements: MovementDataSource;

  /**
   * Paginator
   */
  @ViewChild(MatPaginator) paginator: MatPaginator;

  /**
   * Sort
   */
  @ViewChild(MatSort) sort: MatSort;

  /**
   * Total movements
   */
  totalMovements: number;

  /**
   * Filter object
   */
  filter: any;

  /**
   * BreadcrumbData
   */
  breadcrumbData: IBreadcrumbData;

  /**
   * Filter form
   */
  filterForm = new FormGroup({
    /**
     * Range date picker
     */
    range: new FormGroup({
      start: new FormControl(),
      end: new FormControl(),
    }),
    /**
     * CarPlate
     */
    carPlate: new FormControl(),
    /**
     * Card
     */
    card: new FormControl(),
    result: new FormControl(),
    opening: new FormControl(),
    door: new FormControl(),
    movementId: new FormControl()
  });

  names = MovementsNames;
  expandedElement: any;
  movementBackImageSrc: any;
  movementFrontImageSrc: any;
  contextFrontImageSrc: any;
  facialImageSrc: any;
  contSupImageSrc: any;

  private syncMovementsListSubscription: Subscription;
  public syncronized = true;
  public syncroAction: any;

  doors: { door: number }[];

  /**
   * Constructor
   *
   * @param movementsService MovementsService
   */
  constructor(
    @Inject(CoreConfig.API_BASE_URL) public baseUrl: string,
    private movementsService: MovementsService,
    private snackBar: NotificationService,
    private wsService: WebSocketService
  ) {
    this.filter = {};

    this.breadcrumbData = {
      titles: {
        icon: 'swap_horiz',
        links: [
          {
            text: 'Movimientos',
          },
        ],
      },
      description: 'Aquí puede ver todos los movimientos y puedes filtrarlos',
      buttons: [],
    };

    this.syncroAction = () => this.sync();
    this.socketSincro();

  }

  /**
   * ngOnInit
   */
  ngOnInit(): void {
    this.movements = new MovementDataSource(this.movementsService);
    this.movements.load();

    this.movementsService.getDoors().subscribe((data: { door: number }[]) => {
      this.doors = data;
    });

    this.movementsService.getCount('movementId').subscribe((data: { count: number }) => {
      this.totalMovements = data.count;
    });

    this.filterForm.valueChanges.subscribe((value) => {
      if (value.range != null && value.range.start != null && value.range.end != null) {
        this.filter.createdAt_from = value.range.start.utc().startOf('day').toISOString();
        this.filter.createdAt_to = value.range.end.utc().endOf('day').toISOString();
      }
      if (value.movementId != null) {
        this.filter.movementId = value.movementId;
      }
      if (value.carPlate != null) {
        this.filter.carPlateAssigned = value.carPlate;
      }
      if (value.card != null) {
        this.filter.cardAssigned = value.card;
      }
      if (value.opening != null) {
        this.filter.opening = value.opening;
      }
      if (value.result != null) {
        this.filter.result = value.result;
      }
      if (value.door != null) {
        this.filter.door = value.door;
      }
      this.loadMovements();
    });

    this.socketSincro();
  }

  OnDestroy() {
    this.syncMovementsListSubscription.unsubscribe();
  }

  /**
   * ngAfterViewInit
   */
  ngAfterViewInit(): void {
    this.sort.sortChange.subscribe(() => {
      this.paginator.pageIndex = 0;
    });

    of(this.sort.sortChange, this.paginator.page)
      .pipe(mergeAll())
      .subscribe(() => {
        this.loadMovements();
      });
  }

  /**
   * Load movements
   */
  loadMovements(): void {
    this.movements.load(this.sort.active, this.sort.direction, this.paginator.pageSize, this.paginator.pageIndex, this.filter);

    this.movementsService.getCount('movementId', this.sort.direction, this.paginator.pageSize, this.paginator.pageIndex, this.filter).subscribe((data: { count: number }) => {
      this.totalMovements = data.count;
    });
  }

  keys(jsonObject: any) {
    return Object.keys(jsonObject);
  }

  resetFilterForm() {}

  clear() {
    this.filterForm.reset();
    this.filter = {};
    this.loadMovements();
    this.paginator.pageIndex = 0;
  }

  clearRangeFilter() {
    this.filterForm.get('range').reset();
    delete this.filter.createdAt_from;
    delete this.filter.createdAt_to;
    this.loadMovements();
  }

  setExpandedElement(row: any) {
     // const laneId = Number(row.door);
    if (this.expandedElement === row) {
      this.expandedElement = null;
    } else {
      this.expandedElement = row;
      this.movementFrontImageSrc = `${this.baseUrl}movements/image/${moment(row.createdAt).format('YYYYMMDD')}/${row.movementId}/_F`;
      this.movementBackImageSrc = `${this.baseUrl}movements/image/${moment(row.createdAt).format('YYYYMMDD')}/${row.movementId}/_R`;
      this.contextFrontImageSrc = `${this.baseUrl}movements/image/${moment(row.createdAt).format('YYYYMMDD')}/${row.movementId}/_1`;
      if (row.cardAssigned.length == 20){
        this.facialImageSrc = `${this.baseUrl}movements/image/${moment(row.createdAt).format('YYYYMMDD')}/${row.movementId}/_1`;
      }else {
        this.facialImageSrc = `${this.baseUrl}movements/image/${moment(row.createdAt).format('YYYYMMDD')}/${row.movementId}/_2`;
      }
    }
  }

  sync() {
    this.syncronized = !this.syncronized;
    if (this.syncronized) {
      this.loadMovements();
      this.socketSincro();
    } else {
      this.syncMovementsListSubscription.unsubscribe();
    }
  }

  socketSincro() {
    this.syncMovementsListSubscription = this.wsService.listen('syncMovementList').subscribe(
      (data) => {
        this.loadMovements();
      },
      (err) => {
        this.snackBar.showError(err);
      }
    );
  }
}
